import type { StructureSetId } from '../../changes/ids';
import type { PendingChangesStore } from '../../changes/PendingChangesStore';
import type { PatientPersistenceRef } from '../../persistence/PersistenceRef';
import type { PersistenceRegistry } from '../../persistence/PersistenceRegistry';
import type { Patient } from '../../patient/patient';
import type { DoseSaveService } from '../dose/DoseSaveService';
import type { ImageSaveService } from '../image/ImageSaveService';
import type { RegistrationSaveService } from '../registration/RegistrationSaveService';
import type { StructureSetSaveService } from '../structureSet/StructureSetSaveService';
import type { DatasetSaver, SaveScope } from './DatasetSaver';
import type { SaveEffect } from './SaveEffect';
import type { SaveOutcome, SaveReport } from './SaveOutcome';
import type { SealedSaveIntent } from './SealedSaveIntent';

/**
 * Orchestrates one save attempt across every dataset kind. Owns the
 * attempt: in-flight exclusivity, the {@link SaveIntent} capture, the
 * single engine flush, the stage order, completeSave re-baselining, and
 * the report. The per-kind savers stay independent — one kind's failure
 * never blocks another's entries.
 *
 * Every seal runs first, synchronously (no await after the engine flush,
 * so what a save transmits matches the captured intent even while the
 * user keeps editing); the network stages then run in the order the
 * stage list declares.
 */
export class PatientSaveService {
    private inFlight = false;

    constructor(
        /**
         * The live aggregate — held ONLY to hand to the savers' seal() calls
         * inside the synchronous window. Nothing after the first await may
         * read it; the savers cannot reach it at all.
         */
        private readonly patient: Patient,
        private readonly registry: PersistenceRegistry,
        private readonly pendingChanges: PendingChangesStore,
        private readonly structureSets: StructureSetSaveService,
        private readonly images: ImageSaveService,
        private readonly registrations: RegistrationSaveService,
        private readonly doses: DoseSaveService,
        /**
         * The one writer of save results into session state (registry
         * refs, domain write-backs). Savers return {@link SaveEffect}s
         * as data; only this applier — assembled by the session with
         * the live objects in closure — actually mutates anything.
         */
        private readonly applyEffect: (effect: SaveEffect) => void
    ) {}

    /**
     * The deterministic stage order — the orchestrator's ONE description
     * of cross-kind sequencing. A new dataset kind costs one saver and
     * one entry here.
     *   1. image creates/renames (a new structure set's server skeleton
     *      is created FROM an image series, so a locally created image's
     *      row must exist before the structure-set stage),
     *   2. structure sets over the sealed batch,
     *   3. registrations (deletes → creates → modifications, per-id order),
     *   4. dose deletions,
     *   5. image deletions LAST — leaf-last: structure sets,
     *      registrations, and doses referencing an image must leave the
     *      database before it.
     */
    private stages(): readonly {
        saver: DatasetSaver;
        scope: SaveScope;
    }[] {
        return [
            { saver: this.images, scope: 'upserts' },
            { saver: this.structureSets, scope: 'all' },
            { saver: this.registrations, scope: 'all' },
            { saver: this.doses, scope: 'all' },
            { saver: this.images, scope: 'deletes' },
        ];
    }

    /** False for sessions without connector backing (local files). */
    get canSave(): boolean {
        return this.registry.patientRef !== null;
    }

    async saveAll(
        options: { overwrite?: ReadonlySet<string> },
        flushEngine: () => ReadonlySet<StructureSetId>
    ): Promise<SaveReport> {
        if (this.inFlight) {
            throw new Error('A save is already in progress');
        }
        const patientRef = this.registry.patientRef;
        if (!patientRef) {
            throw new Error(
                'Cannot save: the loaded patient has no connector identity'
            );
        }
        this.inFlight = true;
        try {
            return await this.run(
                options.overwrite ?? null,
                patientRef,
                flushEngine
            );
        } finally {
            this.inFlight = false;
        }
    }

    private async run(
        overwrite: ReadonlySet<string> | null,
        patientRef: PatientPersistenceRef,
        flushEngine: () => ReadonlySet<StructureSetId>
    ): Promise<SaveReport> {
        const intent = this.pendingChanges.beginSaveAttempt();

        // Flush the engine into the domain (a throw propagates: the whole
        // save fails loudly). The returned ids are sets whose edits were
        // LOST to an earlier failed switch-away flush. A failed flush sent
        // nothing, so the attempt settles with no outcomes and its phases
        // do not outlive it.
        let lostFlushIds: ReadonlySet<StructureSetId>;
        try {
            lostFlushIds = flushEngine();
        } catch (error) {
            this.pendingChanges.completeSave(intent, []);
            throw error;
        }

        // INVARIANT: no await between the flush above and this seal — the
        // domain is live and mutable, and everything a save transmits must
        // correspond to the captured intent, not to edits made during the
        // awaits below. Each kind seals its own payload (structure-set
        // bytes, registration matrices); the sealed intent is the only
        // input the savers receive from here on.
        const sealed: SealedSaveIntent = {
            intent,
            lostFlushIds,
            structureSets: this.structureSets.seal(
                this.patient,
                [...intent.state.byStructureSet.values()],
                lostFlushIds
            ),
            registrations: this.registrations.seal(this.patient, [
                ...intent.state.byRegistration.values(),
            ]),
            images: this.images.seal(this.patient, [
                ...intent.state.byImage.values(),
            ]),
        };

        const outcomes: SaveOutcome[] = [];
        for (const { saver, scope } of this.stages()) {
            const results = await saver.saveEntries(
                sealed,
                patientRef,
                overwrite,
                scope
            );
            for (const result of results) {
                // Effects apply WHATEVER the outcome: a create that
                // committed before a later step failed has already earned
                // its ref, and dropping it would make the retry duplicate
                // the row.
                for (const effect of result.effects) {
                    this.applyEffect(effect);
                }
                outcomes.push(result.outcome);
            }
        }

        // The store consumes the report's outcomes directly — the
        // outcomes ARE the record of what persisted, so a separate saved
        // channel could only ever disagree with them.
        this.pendingChanges.completeSave(intent, outcomes);
        return {
            outcomes,
            // Empty-outcome guard: `[].every()` is vacuously true, and a
            // "fully saved" report for a save that saved nothing would
            // violate SR-WOR-H-23-4. Call sites gate on pendingCount > 0
            // today; this must hold even if one stops doing so.
            allSaved:
                outcomes.length > 0 &&
                outcomes.every((o) => o.kind === 'saved'),
        };
    }
}
